import Phaser from 'phaser';
import i18next from 'i18next';
import {
  GAME_WIDTH, GAME_HEIGHT, SCORING, FONT_STACK, CRUMBLE, LADYBIRD,
  PERFECT_STREAK_STEP, STAR_STACK_SLOTS, perfectMilestone,
} from '@/game/config';
import { devFlag, devParam } from '@/game/dev';
import { cascadeOrder, dropOutcome, nearestIndex, shouldCrumble, timeoutAction, wordFallMs } from '@/game/crumble';
import type { BoardRule } from '@/game/rules/BoardRule';
import type { Pack, PackWord, PackSet } from '@/data/packs/types';
import { getBackground } from '@/game/storage';
import { addMuteToggle } from '@/game/ui/muteToggle';
import {
  playPopSound,
  playCrackSound,
  playThudSound,
  playChompSound,
  playRecordSound,
  playRoundCompleteSound,
  startBackdropAmbience,
  stopBackdropAmbience,
  speakRulePrompt,
  cancelRulePrompt,
} from '@/game/audio';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { emphasise } from '@/game/ui/promptEmphasis';
import { createSplitPromptBanner, createStackedPromptBanner } from '@/game/ui/promptBanner';
import { markerForRule } from '@/game/ui/promptMarker';
import { wordTextStyle } from '@/game/fonts';
import { drawContour, hasContour } from '@/game/contours';
import { placeBox, type Rect } from '@/game/wordPlacement';
import { boardIconAssets, wordIconKey } from '@/game/wordIcons';
import { schedulePictureSpeech } from '@/game/pictureSpeak';
import { getActivePack } from '@/game/packs';
import { isHalloweenPack } from '@/game/starGlyphs';
import { buildSpider } from '@/game/ui/halloweenArt';
import type { RoundScene } from './RoundScene';

/**
 * Boards per "Perfekt!" star. Dev/staging only, inert on production (devFlag/devParam):
 *   ?perfect      → after every clean board
 *   ?perfect=3    → after every third
 *   ?perfect=now  → shown at once on board 1, without playing anything (see create())
 * Otherwise verifying the overlay means ten flawless boards in a row. See src/game/dev.ts.
 */
function perfectStreakStep(): number {
  if (!devFlag('perfect')) return PERFECT_STREAK_STEP;
  // 'now' is not a step; it parses to NaN and falls through to 1, which is also what you want
  // for the boards after the preview.
  return Math.max(1, Number(devParam('perfect')) || 1);
}

const CORRECT_PER_BOARD = 4;
const WRONG_PER_BOARD = 4;

/** Extra hold on the round-complete banner of a sentence board, so the readout of the finished
 *  sentence isn't clipped on its last word when the scene restarts (see onTargetRemoved). */
const SENTENCE_READOUT_EXTRA_MS = 200;

export interface BoardInit {
  rule: BoardRule;
  set: PackSet;
  pack: Pack;
  boardNumber: number;
  /** Elapsed board time (ms) at which this board starts to crumble, or null to disable the
   *  Crumbling Board penalty on this board (e.g. the opening boards of a game, before the player's
   *  pace and spread can be estimated). */
  crumbleAfterMs: number | null;
  /** The player's recent pace (median board time, ms), or null before it can be estimated. The
   *  board turns it into a per-tile reading speed and stretches the fall to match, so racing the
   *  collapse is as real a chance for a slow reader as for a fast one. See
   *  specs/adaptive-difficulty.md §2. */
  paceMs: number | null;
}

/** A word still in play, plus the plank/nail scaffolding built lazily when it starts to crack. */
interface Plank {
  label: Phaser.GameObjects.Text;
  word: PackWord;
  /** True when this word matches the rule (a target). Decoys falling off is free. */
  target: boolean;
  /** Resolved = shot or already fallen; ignore for any further collapse handling. */
  resolved: boolean;
  /** Container built at crack time (plank + two nails + reparented label); null until then. */
  container: Phaser.GameObjects.Container | null;
  /** Sentence boards only: mid-shake after a wrong tap, so a drum-roll can't spend three lives. */
  bouncing: boolean;
}

/** One slot of the sentence strip: the empty box and the Text that appears once it is filled. */
interface Slot {
  text: Phaser.GameObjects.Text;
  x: number;
  y: number;
}

export class BoardScene extends Phaser.Scene {
  private rule!: BoardRule;
  private set!: PackSet;
  private pack!: Pack;
  private boardNumber = 1;
  private remainingTargets = 0;
  private advancing = false;
  /** Crumbling Board state (specs/crumbling-board.md). `crumbling` = the slow-play timeout has
   *  fired (either the falling-words collapse or the ladybird); it fires at most once per board. */
  private crumbleAfterMs: number | null = null;
  private paceMs: number | null = null;
  /** Set once the board is laid out and the tile count is known — see BoardInit.paceMs. */
  private fallMs = CRUMBLE.fallMs;
  private crumbling = false;
  private planks: Plank[] = [];
  /** The ladybird alternative (§4b): the crawling bug, or null when this board didn't roll it. */
  private ladybird: Phaser.GameObjects.Container | null = null;
  /**
   * Set when the user's pointerdown was on the help button. The scene-level pointerdown
   * handler that plays the pop sound checks and clears this so we don't play a pop
   * when the player opens the help panel.
   */
  private suppressNextPopSound = false;
  /** The picture-banner image on a picture board, else null. See createPictureBanner. */
  private pictureBanner: Phaser.GameObjects.Image | null = null;
  /** Sentence boards (specs/sentence-order-mode.md): the empty slots, left to right. */
  private slots: Slot[] = [];
  /** How many of them are filled — the next correct hit flies into `slots[placedSlots]`. */
  private placedSlots = 0;

  constructor() {
    super({ key: 'Board' });
  }

  init(data: BoardInit): void {
    this.rule = data.rule;
    this.set = data.set;
    this.pack = data.pack;
    this.boardNumber = data.boardNumber;
    this.remainingTargets = 0;
    this.advancing = false;
    this.suppressNextPopSound = false;
    this.crumbleAfterMs = data.crumbleAfterMs;
    this.paceMs = data.paceMs;
    this.crumbling = false;
    this.planks = [];
    this.ladybird = null;
    this.pictureBanner = null;
    this.slots = [];
    this.placedSlots = 0;
  }

  preload(): void {
    // Picture boards: load only THIS board's target icons (at most CORRECT_PER_BOARD) — the same
    // load-what-you-show policy the backdrop keeps in BackdropScene. They're precached by the
    // service worker, so this is a cache read rather than a network request.
    // specs/picture-word-mode.md §4.4.
    //
    // Anything queued here holds the whole scene in LOADING, board included, so this is the
    // fallback rather than the normal path: RoundScene.warmBoardAssets loads the same icons while
    // the finished board is still on screen, and a warmed board finds them all cached and skips
    // straight to create(). What's left for here is the first board of a round and any board whose
    // warm-up ran out of time.
    for (const { key, url } of boardIconAssets(this.rule, this.set)) {
      if (!this.textures.exists(key)) this.load.image(key, url);
    }
  }

  /** True when this board's rule wants a picture in the banner instead of prompt text. */
  private isPictureBoard(): boolean {
    return typeof this.rule.iconId === 'string';
  }

  create(): void {
    // Backdrop ambience: synthesized loop matched to the chosen backdrop.
    // Birds for nature, classroom murmur for school, silence for black.
    // Stopped automatically on scene shutdown / cancel in endRound / backToMenu.
    startBackdropAmbience(getBackground());

    // Hide the system cursor for the duration of the board — the crosshair is
    // the pointer during gameplay. CSS (body.in-game) handles this reliably;
    // setDefaultCursor('none') alone doesn't apply until the first pointermove.
    document.body.classList.add('in-game');

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      document.body.classList.remove('in-game');
      // Keep the ambience playing when we're only restarting for the next board (advanceBoard does a
      // scene.restart, and the new instance's startBackdropAmbience is a no-op for the same bed, so
      // the loop continues unbroken). Only stop it when actually leaving the run — back to menu or
      // game-over — where `advancing` is false.
      if (!this.advancing) stopBackdropAmbience();
      cancelRulePrompt();
    });

    // Mute toggle (bottom-left, same spot as the menu). Applies live during play and suppresses the
    // pop the scene-level pointerdown would otherwise fire on this tap (like the help button).
    addMuteToggle(this, {
      depth: 50,
      onToggle: (muted) => {
        this.suppressNextPopSound = true;
        if (muted) stopBackdropAmbience();
        else startBackdropAmbience(getBackground());
      },
    });

    // The backdrop is NOT drawn here: it belongs to BackdropScene, which RoundScene launches once
    // per round and never restarts, so it survives the restart this scene does between boards.

    // Rule banner + help "?" button slide in together from the top. The help button used
    // to be created in the tween's onComplete, which meant the ? was invisible AND
    // non-clickable for the first 600ms — a quick player would click "nothing".
    // Tonal packs (specs/tonal-modes.md §3.1/§3.4): the target tone's pitch-contour arrow rides
    // INSIDE the prompt pill, right after the text. Reserve room on the right and shift the text
    // left so the text+arrow unit stays centred.
    const showContour = !!(this.rule.contour && hasContour(this.rule.contour));
    const ARROW_GAP = 12;
    const ARROW_W = 52;
    const extraRight = showContour ? ARROW_GAP + ARROW_W : 0;

    // Picture boards swap the prompt text for the target word's picture, inside the same charcoal
    // pill the rule text sits in — a white-backgrounded plate would read as a sticker pasted onto
    // the board, and glares on the default starfield backdrop. specs/picture-word-mode.md §2.5.
    // Falls back to the text banner when the icon is missing (§2.4), so a shipping accident
    // degrades to a playable class-match board rather than an unanswerable one.
    const picture = this.isPictureBoard() ? this.createPictureBanner() : null;

    // The rule word gets picked out of the sentence. Everything around it is boilerplate — every
    // board says "Schnappe dir alle …" — so a child reading the banner (no voice for this
    // language, muted device, or the readout already cancelled by their first tap) is wading
    // through four identical words to find the one that carries the rule. Phaser Text has no
    // inline styling of any kind, so this is three Texts laid out in a row; when no key word can
    // be identified it falls back to the plain single Text below.
    const ruleClass = this.pack.classes.find((c) => c.id === this.rule.classId);
    // A prompt carrying a newline is a sentence-over-question banner (comprehension mode,
    // specs/comprehension-mode.md §5) and takes the stacked path — checked BEFORE emphasise(),
    // never as a fallthrough from it. emphasise() happens to return null for the shipped
    // comprehension pack, but only because no sentence in it contains the stem "Ant" (from the
    // class label "Antwort"); a sentence about an *Antenne* would silently route a two-line prompt
    // into the split banner, which lays its three Texts along ONE baseline and would fold the
    // sentence and the question on top of each other.
    const stacked = !picture && this.rule.prompt.includes('\n');
    const keyWord = picture || stacked
      ? null
      : emphasise(this.rule.prompt, ruleClass?.label, ruleClass?.emphasis);

    const banner = picture
      ? null
      : stacked
        ? createStackedPromptBanner(
          this,
          this.rule.prompt.slice(0, this.rule.prompt.indexOf('\n')),   // the sentence
          this.rule.prompt.slice(this.rule.prompt.indexOf('\n') + 1),  // the question about it
          GAME_WIDTH / 2 - extraRight / 2, -40,
        )
        : keyWord
          ? createSplitPromptBanner(
            this, keyWord, GAME_WIDTH / 2 - extraRight / 2, -40, '36px',
            // "Der, Die, Das" puts the classroom shape for the gender in front of the article.
            markerForRule(this.pack.id, this.rule.classId),
          )
          : this.add.text(GAME_WIDTH / 2 - extraRight / 2, -40, this.rule.prompt, {
            fontFamily: FONT_STACK,
            fontSize: '36px', color: '#ffd166', padding: { x: 18, y: 10 },
          }).setOrigin(0.5);
    const bannerBg = banner ? addRoundedLabelBg(this, banner, { padRight: extraRight }) : picture!.bg;
    const anchor = banner
      ? { x: banner.x, y: banner.y, width: banner.width }
      : { x: picture!.image.x, y: picture!.image.y, width: picture!.width };
    const pillRightEdge = anchor.x + anchor.width / 2 + extraRight;
    const helpButton = this.addHelpButton(anchor, pillRightEdge);

    let contour: Phaser.GameObjects.Container | null = null;
    if (showContour && banner) {
      contour = drawContour(this, this.rule.contour!);
      if (contour) {
        contour.setPosition(banner.x + banner.width / 2 + extraRight / 2, -40);
        contour.setDepth(banner.depth);
      }
    }

    const slideIn: Phaser.GameObjects.GameObject[] = [bannerBg];
    if (banner) slideIn.push(banner);
    if (picture) slideIn.push(picture.image);
    if (helpButton) slideIn.push(helpButton);
    if (contour) slideIn.push(contour);
    // A picture pill is 174px tall — far taller than the text banner — so landing it at the text
    // banner's y=60 hung its top 87-60=27px above the canvas edge, cropping a slice off the top of
    // every picture the game showed. How much it showed depended on the icon: art with padding
    // survived, art that filled its box lost its head. 87 puts the pill's top edge exactly at 0.
    const PICTURE_BANNER_Y = 87;
    // A stacked banner is two or three lines deep, and which one depends on whether the sentence
    // wrapped — so the picture pill's fixed 87 cannot serve it. Same rule, computed: drop it far
    // enough that the pill's top edge clears the canvas by TOP_MARGIN, and never lift a short
    // one above the usual 60.
    const TOP_MARGIN = 6;
    const stackedY = stacked ? Math.max(60, banner!.height / 2 + TOP_MARGIN) : 60;
    this.tweens.add({
      targets: slideIn,
      y: picture ? PICTURE_BANNER_Y : stackedY, duration: 600, ease: 'Cubic.easeOut',
      onComplete: () => {
        // Read the rule aloud in the active UI language once the banner is settled.
        // Cancel-on-input is wired in the scene-level pointerdown handler below.
        // A picture board speaks the pictured WORD rather than the rule text — the picture is the
        // prompt, and for a kindergarten player hearing the word is the bridge into reading it.
        if (picture) { this.speakPictureWord(); return; }
        speakRulePrompt(this.rule.prompt, i18next.language);
      },
    });

    // Sample correct + wrong words for this rule, restricted to the candidate pool —
    // banner-driven modes (Thai classifier, JA/KO particles) limit candidates to one class,
    // so the bannered context word (the noun in classifier mode) never becomes a flying target.
    const set = this.set;
    const isCandidate = (w: PackWord) => this.rule.isCandidate ? this.rule.isCandidate(w) : true;
    // Sampling and completion-counting ask "will this be a target on this board?", NOT "is it
    // correct right now". They are the same question for every class rule, but on a picture board
    // `matches` is true for only the ONE word currently in the banner — sampling on it would put a
    // single target on an eight-tile board and end the round after one smash.
    const isTarget = (w: PackWord) => this.rule.willBeTarget ? this.rule.willBeTarget(w) : this.rule.matches(w);
    const candidatesPool = set.words.filter(isCandidate);
    const correctPool = candidatesPool.filter(isTarget);
    const wrongPool = candidatesPool.filter((w) => !isTarget(w));
    const sample: PackWord[] = Phaser.Utils.Array.Shuffle([
      ...Phaser.Utils.Array.Shuffle([...correctPool]).slice(0, CORRECT_PER_BOARD),
      ...Phaser.Utils.Array.Shuffle([...wrongPool]).slice(0, WRONG_PER_BOARD),
    ]);

    // Only target words count toward completion; wrong words are decoys.
    this.remainingTargets = sample.filter(isTarget).length;

    // The rescue window is per TILE, so it can only be worked out now that we know how many tiles
    // this board actually laid out (an arcade set is sampled down to eight here).
    this.fallMs = wordFallMs(this.paceMs, sample.length);

    // A picture rule is built from the words the progression engine picked, but the board decides
    // what actually gets tiles. If the two ever disagree (fewer slots than queued targets), drop the
    // unshown ones so the banner can never ask for a word that was never on screen.
    if (this.rule.resolve) {
      const shown = new Set(sample.map((w) => (typeof w.id === 'string' ? w.id : w.text)));
      for (const w of set.words) {
        const id = typeof w.id === 'string' ? w.id : w.text;
        if (isTarget(w) && !shown.has(id)) this.rule.resolve(w);
      }
    }

    // Tell the round which tiles are on screen so arcade lessons can track per-word mastery
    // (no-op in reading mode, where the engine already chose and recorded these words).
    (this.scene.get('Round') as RoundScene).onBoardShown(sample);

    // Sentence boards draw the slot strip first: it owns the bottom of the screen, and the tiles
    // have to be told to keep off it.
    this.buildSlotStrip();

    const placed: Rect[] = [];
    const PADDING = 24;
    const HIT_PAD = 18;   // enlarge each word's hit area beyond its glyphs so near-misses count
    const MARGIN_X = 140;
    const MARGIN_TOP = 180;
    // The strip is 68px tall with its role labels below, centred at GAME_HEIGHT - 98. 190 clears
    // both with room for the shake in bounceTile().
    const MARGIN_BOTTOM = this.slots.length > 0 ? 190 : 100;

    for (const word of sample) {
      const label = this.add.text(0, 0, word.text, {
        fontFamily: FONT_STACK,
        fontSize: '40px',
        color: '#ffffff',
        ...wordTextStyle(i18next.language),
      }).setOrigin(0.5);

      // Forgiving hit area: pad the text bounds by HIT_PAD on every side (origin is 0.5, so the
      // hit rect is in the text's top-left-anchored local space), then grow the rect another 5%
      // (centered). The growth is capped at PADDING - HIT_PAD per side so padded hit areas still
      // can't overlap neighbours (words are spaced >= 2*PADDING apart).
      const hitExtraX = Math.min(PADDING - HIT_PAD, (label.width + HIT_PAD * 2) * 0.025);
      const hitExtraY = Math.min(PADDING - HIT_PAD, (label.height + HIT_PAD * 2) * 0.025);
      label.setInteractive(
        new Phaser.Geom.Rectangle(
          -HIT_PAD - hitExtraX, -HIT_PAD - hitExtraY,
          label.width + (HIT_PAD + hitExtraX) * 2, label.height + (HIT_PAD + hitExtraY) * 2,
        ),
        Phaser.Geom.Rectangle.Contains,
      );
      label.input!.cursor = 'pointer';

      const w = label.width + PADDING * 2;
      const h = label.height + PADDING * 2;
      const minX = MARGIN_X + w / 2;
      const maxX = GAME_WIDTH - MARGIN_X - w / 2;
      const minY = MARGIN_TOP + h / 2;
      const maxY = GAME_HEIGHT - MARGIN_BOTTOM - h / 2;

      // Choose a non-overlapping spot. placeBox always records the chosen rect (even in the rare
      // crowded fallback, where it returns the least-overlapping spot) so later words avoid it too.
      const { cx, cy, rect } = placeBox(
        w, h, { minX, maxX, minY, maxY }, placed, (a, b) => Phaser.Math.Between(a, b),
      );
      placed.push(rect);
      label.setPosition(cx, cy);

      // `target` is "will be a target at some point this board", which for a class rule is just
      // `matches`. A picture rule needs the two split: every pictured word is a target, but only
      // the one currently in the banner is CORRECT — see the pointerdown handler below.
      const target = this.rule.willBeTarget ? this.rule.willBeTarget(word) : this.rule.matches(word);
      const plank: Plank = { label, word, target, resolved: false, container: null, bouncing: false };
      this.planks.push(plank);

      label.on('pointerdown', () => {
        if (this.advancing || plank.resolved || plank.bouncing) return;

        // The tap sound is played HERE, not left to the scene-level handler below. Phaser emits
        // GAMEOBJECT_POINTER_DOWN before POINTER_DOWN, so this runs first — and this handler can
        // clear the board's last target or spend the last life, either of which sets `advancing`
        // and disables input. Both make the scene-level handler skip (it guards on `advancing`,
        // and InputPlugin.isActive() is false once input is disabled), so the pop went missing on
        // exactly the taps that mattered most: the one that finished the board, and the one that
        // ended the run. Clicking empty space kept its sound, which is why it read as words
        // being silent.
        playPopSound();
        // …and stop the catch-all doubling it for this same tap. Cleared on the next tick as well
        // as by the handler itself, so a swallowed handler can never leave the flag stuck on.
        this.suppressNextPopSound = true;
        this.time.delayedCall(0, () => { this.suppressNextPopSound = false; });

        // Evaluated at the moment of the tap, not at spawn: on a picture board a word that will be
        // asked later is wrong RIGHT NOW. Identical to `target` for every class-match rule.
        const correct = this.rule.matches(word);
        const round = this.scene.get('Round') as RoundScene;

        // Sentence boards: a wrong tap costs a life but must NOT consume the tile — it may hold a
        // word the sentence still needs, and destroying it would leave the strip with a hole the
        // player can never fill. The tile shakes and stays tappable. specs/sentence-order-mode.md §4.
        if (!correct && this.rule.keepsTilesOnWrongTap) {
          round.registerHit(false, SCORING.correctHit);
          round.onWordResolved(word, false);
          this.bounceTile(plank);
          return;
        }

        plank.resolved = true;   // also cancels any in-progress collapse for this word
        label.disableInteractive();

        round.registerHit(correct, SCORING.correctHit);
        round.onWordResolved(word, correct);   // per-word mastery for the active lesson

        // The word has left the screen either way, so a picture rule must drop it from its queue —
        // otherwise the banner could later ask for a tile that is no longer there and the board
        // would be unfinishable. Counts against the board's remaining targets for the same reason.
        // A sentence rule advances its slot index off the same call.
        if (target) {
          this.rule.resolve?.(word);
          this.refreshPictureBanner();
        }

        // Fade whatever is on screen for this word — the bare label, or the plank container if it
        // was already cracking. Racing the collapse: shooting a target before it falls saves the life.
        const dying = plank.container ?? label;
        this.tweens.killTweensOf(dying);
        // …except on a sentence board, where a correct word does not vanish: it flies down into the
        // slot it belongs in, which is the whole visible point of the mode.
        const slot = correct ? this.claimSlot() : null;
        if (slot) {
          this.tweens.add({
            targets: dying,
            x: slot.x, y: slot.y, scale: 0.85,
            duration: 260, ease: 'Cubic.easeInOut',
            onComplete: () => { dying.destroy(); this.fillSlot(slot, word.text); },
          });
        } else {
          this.tweens.add({
            targets: dying,
            alpha: 0,
            scale: correct ? 1.4 : 0.6,   // grow on a right answer, shrink on a wrong one
            duration: 200,
            onComplete: () => dying.destroy(),
          });
        }

        if (target) this.onTargetRemoved();
      });
    }

    this.input.setDefaultCursor('none');
    // The crosshair lives in the HUD scene (which renders above Board) so it draws on top of
    // the HUD's menu button instead of being hidden behind it.

    this.input.on('pointerdown', () => {
      if (this.advancing) return;
      // Player interacted — silence any in-progress rule readout immediately so the
      // voice doesn't talk over gameplay.
      cancelRulePrompt();
      if (this.suppressNextPopSound) {
        this.suppressNextPopSound = false;
        return;
      }
      playPopSound();
    });

    (this.scene.get('Round') as RoundScene).startBoardTimer();

    // Dev/staging only: `?perfect=now` throws the "Perfekt!" star up immediately on the first
    // board, so its layout and timing can be judged without playing a clean round first. It does
    // NOT advance the board or touch the streak — purely a look at the overlay.
    if (devParam('perfect') === 'now' && this.boardNumber === 1) {
      this.input.enabled = false;
      this.showPerfectStar(PERFECT_STREAK_STEP, () => { this.input.enabled = true; });
    }

    // Dev/staging only: `?truestar` plays the full-stack send-off (rocket + confetti) at once on
    // the first board. Earning it for real needs every slot of the stack filled in one game, which
    // is not something you can sit down and reproduce.
    if (devFlag('truestar') && this.boardNumber === 1) {
      this.input.enabled = false;
      this.showTrueStar(STAR_STACK_SLOTS, () => { this.input.enabled = true; });
    }
  }

  /**
   * Build the picture banner: the current target word's icon inside the same charcoal pill the rule
   * text uses. Returns null when the icon texture is missing, which routes the caller to the plain
   * text banner (specs/picture-word-mode.md §2.4) — the board is then an ordinary class-match round
   * rather than an unanswerable one.
   *
   * PIC + PAD are sized so the pill sits fully on screen AND its bottom edge clears the existing
   * MARGIN_TOP of 180 — word tiles can never end up underneath it, so no layout constant changes.
   * With the pill landing at PICTURE_BANNER_Y (87, see createBoard): top edge 87 - 87 = 0,
   * bottom edge 87 + 87 = 174.
   *
   * PAD is 12 rather than the text banner's roomier padding for exactly that reason: at 16 the
   * pill is 182 tall, and no landing y can both clear the top edge and stay above 180.
   */
  private createPictureBanner(): { image: Phaser.GameObjects.Image; bg: Phaser.GameObjects.NineSlice; width: number } | null {
    const PIC = 150;
    const PAD = 12;
    const id = this.rule.iconId;
    if (!id || !this.textures.exists(wordIconKey(id))) {
      console.warn(`[Board] word icon missing for "${id}" — falling back to the text prompt.`);
      return null;
    }

    const size = PIC + PAD * 2;
    // The same pill the text banner wears, sized square around the picture — and, like it, a
    // nine-slice rather than a Graphics, which Phaser would re-tessellate on every frame of the
    // board (see ui/roundedLabelBg). Fed a synthetic box because there is no Text here to measure:
    // a centred square at the banner's position, one depth above what the plate should sit at.
    const bg = addRoundedLabelBg(this, {
      x: GAME_WIDTH / 2, y: -40, width: size, height: size, originX: 0.5, originY: 0.5, depth: 1,
    }, { radius: 18 });

    const image = this.add.image(GAME_WIDTH / 2, -40, wordIconKey(id)).setDisplaySize(PIC, PIC);
    image.setDepth(bg.depth + 1);
    this.pictureBanner = image;
    return { image, bg, width: size };
  }

  /**
   * Say the word whose picture is showing, a beat after it appears (§2.2). The delay is deliberate:
   * spoken instantly, the audio arrives before the child has looked at the picture.
   *
   * Phaser clears the scene's timers on shutdown/restart, so a pending call can't fire into a dead
   * board. A tap mid-readout cancels it through the scene-level cancelRulePrompt().
   */
  private speakPictureWord(): void {
    schedulePictureSpeech(this.time, this.rule.iconId, this.set.words,
      (text) => speakRulePrompt(text, i18next.language));
  }

  /**
   * Swap the banner to the next word's picture after a target leaves the board. Held rather than
   * blanked when the queue is spent (§2.1), so the last picture stays up until the board-complete
   * banner takes over.
   */
  private refreshPictureBanner(): void {
    const image = this.pictureBanner;
    if (!image) return;
    const id = this.rule.iconId;
    if (!id || !this.textures.exists(wordIconKey(id))) return;
    if (image.texture.key === wordIconKey(id)) return;

    // Cross-fade rather than a hard cut, so the pill reads as one object changing rather than a
    // new banner arriving.
    this.tweens.add({
      targets: image, alpha: 0, duration: 110, ease: 'Quad.easeIn',
      onComplete: () => {
        if (!image.active) return;
        image.setTexture(wordIconKey(id));
        image.setDisplaySize(150, 150);
        this.tweens.add({ targets: image, alpha: 1, duration: 140, ease: 'Quad.easeOut' });
        this.speakPictureWord();   // the next word, once its picture is actually on screen
      },
    });
  }

  /**
   * The sentence strip along the bottom (specs/sentence-order-mode.md §3): one box per word of the
   * sentence, each labelled with the role that belongs in it, plus the pre-filled first slot when
   * the sentence has one. No-op on every other mode — `slotRoles` is the flag.
   *
   * Every empty slot is drawn the SAME width, sized to the widest thing any of them could hold.
   * Sizing each slot to its own word would let a child match tile widths to slot widths and get the
   * sentence right without reading it — the one shortcut this mode has to be careful about, since
   * unlike a class-match board there is exactly one right answer per slot.
   */
  private buildSlotStrip(): void {
    const roles = this.rule.slotRoles;
    if (!roles || roles.length === 0) return;
    const vorgabe = this.rule.slotVorgabe ?? '';

    const SLOT_H = 68;
    const GAP = 14;
    const PAD_X = 20;
    const Y = GAME_HEIGHT - 104;
    const tokenStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: FONT_STACK, fontSize: '34px', color: '#ffffff',
      ...wordTextStyle(i18next.language),
    };
    // Outlined like the word tiles, and for the same reason: the strip sits over whichever backdrop
    // the player chose, and on the classroom one a plain grey caption vanished into the floorboards.
    const roleStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: FONT_STACK, fontSize: '19px', color: '#dbe6ff', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 4,
    };
    const measure = (t: string, style: Phaser.Types.GameObjects.Text.TextStyle): number => {
      const probe = this.add.text(0, 0, t, style);
      const w = probe.width;
      probe.destroy();
      return w;
    };

    let widest = 0;
    for (const w of this.set.words) {
      if (this.rule.willBeTarget?.(w)) widest = Math.max(widest, measure(w.text, tokenStyle));
    }
    for (const r of roles) widest = Math.max(widest, measure(r, roleStyle));
    const slotW = widest + PAD_X * 2;
    const widths = roles.map(() => slotW);
    if (vorgabe) widths.unshift(measure(vorgabe, tokenStyle) + PAD_X * 2);

    const total = widths.reduce((a, b) => a + b, 0) + GAP * (widths.length - 1);
    let x = GAME_WIDTH / 2 - total / 2;

    const g = this.add.graphics().setDepth(4);
    widths.forEach((w, i) => {
      const isVorgabe = !!vorgabe && i === 0;
      // The given slot is solid and lighter; the empty ones are dark holes waiting to be filled.
      // Both are near-opaque with a bright rim: at 0.72 alpha and a dim border the empty slots read
      // as smudges on the classroom backdrop rather than as boxes to aim at.
      g.fillStyle(isVorgabe ? 0x2b3252 : 0x0b0f1a, isVorgabe ? 1 : 0.9);
      g.fillRoundedRect(x, Y - SLOT_H / 2, w, SLOT_H, 12);
      g.lineStyle(4, isVorgabe ? 0x8b96c4 : 0x7c88b8, 1);
      g.strokeRoundedRect(x, Y - SLOT_H / 2, w, SLOT_H, 12);

      const cx = x + w / 2;
      if (isVorgabe) {
        // No role label under the given slot: it is visibly filled, so naming it would only be one
        // more word to read — and one more string to translate when the lesson leaves de-CH.
        this.add.text(cx, Y, vorgabe, tokenStyle).setOrigin(0.5).setDepth(5);
      } else {
        this.add.text(cx, Y + SLOT_H / 2 + 16, roles[vorgabe ? i - 1 : i], roleStyle)
          .setOrigin(0.5).setDepth(5);
        const text = this.add.text(cx, Y, '', tokenStyle).setOrigin(0.5).setDepth(5).setVisible(false);
        this.slots.push({ text, x: cx, y: Y });
      }
      x += w + GAP;
    });
  }

  /** The slot the next correct hit flies into, claimed synchronously so rapid taps can't share one. */
  private claimSlot(): Slot | null {
    if (this.placedSlots >= this.slots.length) return null;
    return this.slots[this.placedSlots++];
  }

  /** Reveal a slot's word once the tile has finished flying into it. */
  private fillSlot(slot: Slot, text: string): void {
    if (!slot.text.active) return;
    slot.text.setText(text).setVisible(true);
    slot.text.setScale(1.25);
    this.tweens.add({ targets: slot.text, scale: 1, duration: 160, ease: 'Back.easeOut' });
  }

  /**
   * Wrong tap on a sentence board: shake the word and leave it there (§4). The shake rides on the
   * LABEL's x, never the plank container's, so it cannot fight the crumble's fall tween — once a
   * word has cracked, its label is a child of the container and this moves it locally.
   */
  private bounceTile(plank: Plank): void {
    plank.bouncing = true;
    const label = plank.label;
    const baseX = label.x;
    label.setColor('#ff9878');
    this.tweens.add({
      targets: label,
      x: { from: baseX - 9, to: baseX + 9 },
      duration: 55, yoyo: true, repeat: 2, ease: 'Sine.easeInOut',
      onComplete: () => {
        plank.bouncing = false;
        if (!label.active) return;
        label.x = baseX;
        label.setColor('#ffffff');
      },
    });
  }

  private addHelpButton(banner: { x: number; y: number; width: number }, pillRightEdge?: number): Phaser.GameObjects.Container | null {
    const classDef = this.pack.classes.find((c) => c.id === this.rule.classId);
    // The "?" button only appears when the board's target class has an explanation, so a missing
    // explanation makes it "show nothing" — invariant locked by
    // src/data/help-button-explanations.test.ts (every askable class must carry an explanation).
    // (If a click on the visible "?" seems to do nothing, see the hit-area note below — the circle's
    // lower half was once dead due to a Phaser displayOrigin quirk.)
    if (!classDef?.explanation) return null;   // no help text → no button

    // Tracks vertical position with the banner. Horizontal offset is fixed to the right of the
    // pill (which may be wider than the text when a tonal arrow is tucked inside) so a long
    // prompt pushes the button further out but never overlaps it.
    const x = (pillRightEdge ?? banner.x + banner.width / 2) + 36;
    const help = this.add.container(x, banner.y);
    const circle = this.add.circle(0, 0, 26, 0x7ec4ff).setStrokeStyle(3, 0xffffff);
    const text = this.add.text(0, -2, '?', {
      fontFamily: FONT_STACK,
      fontSize: '40px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5);
    help.add([circle, text]);

    // Hit area is intentionally larger than the visible 52×52 circle — kid-friendly on touch,
    // and avoids near-misses on mouse.
    const HIT = 80;
    help.setSize(HIT, HIT);
    // Hit area anchored at (0,0), NOT centered on the origin: setSize() gives the container a
    // displayOrigin of (HIT/2, HIT/2), and Phaser ADDS displayOrigin to the local point before
    // hit-testing. So a top-left (0,0,HIT,HIT) rectangle lands centered on the "?" circle; a
    // centered (-HIT/2,-HIT/2,…) rectangle double-offsets and shifts the clickable zone up-and-left
    // of the circle (its lower half becomes dead — measured, see the git history for this line).
    help.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, HIT, HIT),
      Phaser.Geom.Rectangle.Contains,
    );
    // Desktop affordance — the crosshair already signals "clickable" but a hand cursor
    // helps when the player is hovering before clicking.
    this.input.setDefaultCursor('none');   // re-asserted on next pointermove
    help.input!.cursor = 'pointer';
    (help as Phaser.GameObjects.Container & { name?: string }).name = 'help-button';

    help.on('pointerdown', () => {
      if (this.advancing) return;
      // Stop event propagation so the scene-level pointerdown handler doesn't play the
      // pop sound on a help-button click. Without this the player hears a pop when
      // they open the help panel.
      this.suppressNextPopSound = true;
      this.scene.pause();
      this.scene.pause('Round');
      this.scene.pause('HUD');
      this.scene.launch('Help', { classDef });
    });

    return help;
  }

  /**
   * Crumbling Board trigger (specs/crumbling-board.md §3). Phaser skips update() while the scene
   * is paused, so time spent in the Help dialog neither ticks toward the threshold here nor via
   * the pause-aware RoundScene.elapsedMs() — the required §3.1 clock-pause falls out for free.
   */
  update(): void {
    if (this.crumbling || this.advancing || this.crumbleAfterMs === null) return;
    const round = this.scene.get('Round') as RoundScene;
    if (this.planks.some((p) => !p.resolved) && shouldCrumble(round.elapsedMs(), this.crumbleAfterMs)) {
      this.crumbling = true;
      // Roll the slow-play action: 20% ladybird, else the falling-words collapse (§4b).
      // `?bug` always sends the bug in (dev/staging only) — a 20% roll is a poor way to look at it.
      if (devFlag('bug') || timeoutAction(Math.random()) === 'ladybird') this.startLadybird();
      else this.startCollapse();
    }
  }

  /** Begin the staggered collapse: each still-standing plank cracks off in reading order, one
   *  after another. Timers live on this scene's clock, so they freeze if the Help dialog opens. */
  private startCollapse(): void {
    const alive = this.planks.filter((p) => !p.resolved);
    // Randomized gap per plank (accumulated) so the collapse doesn't tick like a metronome.
    let at = 0;
    cascadeOrder(alive.map((p) => ({ x: p.label.x, y: p.label.y }))).forEach((idx, step) => {
      if (step > 0) at += Phaser.Math.Between(CRUMBLE.cascadeMinMs, CRUMBLE.cascadeMaxMs);
      this.time.delayedCall(at, () => {
        const plank = alive[idx];
        if (this.advancing || plank.resolved) return;
        this.crackPlank(plank);
      });
    });
  }

  /** One word's fall, like a board nailed at two corners (motion only — nothing is drawn around
   *  the word). The first corner gives way and the word tilts, pivoting on the other corner; a beat
   *  later the second corner lets go and it falls slowly, tumbling off the bottom. Reparented into
   *  an invisible container so the tilt can pivot about a corner rather than the word's centre.
   *  Stays shootable throughout — the player can race the collapse. */
  private crackPlank(plank: Plank): void {
    if (plank.resolved || this.advancing) return;
    const { label } = plank;

    // Pivot = the word's top-right corner (in world space). The container origin sits there and the
    // label hangs down-and-left of it, so a small clockwise rotation drops the left corner.
    const hw = label.width / 2;
    const hh = label.height / 2;
    const container = this.add.container(label.x + hw, label.y - hh);
    label.setPosition(-hw, hh);
    container.add(label);
    plank.container = container;

    // First corner gives way → the word tilts, hanging from the other corner.
    playCrackSound();
    this.tweens.add({ targets: container, angle: 14, duration: 300, ease: 'Sine.easeOut' });

    // Beat, then the second corner lets go → it falls slowly, accelerating (gravity-like) off-screen.
    this.time.delayedCall(CRUMBLE.cornerGapMs, () => {
      if (plank.resolved || this.advancing) return;
      playCrackSound();
      this.tweens.add({
        targets: container,
        y: GAME_HEIGHT + label.height * 2,
        angle: 120,
        duration: this.fallMs,
        ease: 'Quad.easeIn',
        onComplete: () => this.handleFellOff(plank),
      });
    });
  }

  /** A plank finished falling off the bottom. A target costs a life (and resolves toward
   *  completion so the board can't soft-lock); a decoy is free. No-op once the board has advanced
   *  (e.g. the clearing hit already completed it) or if the word was shot mid-fall. */
  private handleFellOff(plank: Plank): void {
    if (plank.resolved || this.advancing) return;
    plank.resolved = true;
    plank.container?.destroy();
    plank.container = null;
    playThudSound();

    const outcome = dropOutcome(plank.target);
    const round = this.scene.get('Round') as RoundScene;
    if (outcome.loseLife) round.onTargetDropped(plank.word);   // may end the round at 0 lives
    // A dropped target is gone from the screen, so a picture rule must drop it from its queue too —
    // otherwise the banner would keep asking for a word that already fell. specs/picture-word-mode.md §2.1.
    if (outcome.resolvesTarget) {
      this.rule.resolve?.(plank.word);
      this.refreshPictureBanner();
    }
    // Only advance the board while it's still alive — onTargetDropped may have just ended the round.
    if (outcome.resolvesTarget && round.lives > 0 && !this.advancing) this.onTargetRemoved();
  }

  // ---------- Ladybird: the alternative slow-play timeout action (§4b) ----------

  /** Send in the ladybird: spawn it off the bottom edge, then have it crawl to the nearest word. */
  private startLadybird(): void {
    this.ladybird = this.buildLadybird(GAME_WIDTH / 2, GAME_HEIGHT + 40);
    this.crawlToNextWord();
  }

  /** A little drawn ladybird (no asset): red domed body split into two wings, black head with two
   *  antennae, spots. Scaled up 10% overall. */
  private buildLadybird(x: number, y: number): Phaser.GameObjects.Container {
    // The Halloween lesson sends in a spider instead (same crawl, same bites — see halloweenArt.ts).
    if (isHalloweenPack(getActivePack())) return buildSpider(this, x, y);
    const c = this.add.container(x, y).setDepth(60);
    const body = this.add.ellipse(0, 0, 46, 56, 0xd11f1f).setStrokeStyle(3, 0x1a1a1a);
    const split = this.add.rectangle(0, 2, 3, 50, 0x1a1a1a);
    const head = this.add.ellipse(0, -28, 30, 22, 0x1a1a1a);
    // Two antennae rising from the head — a thin stalk angled outward with a little club tip.
    const antennae = [
      this.add.rectangle(-7, -45, 2.5, 16, 0x1a1a1a).setRotation(-0.45),
      this.add.circle(-12, -52, 3.5, 0x1a1a1a),
      this.add.rectangle(7, -45, 2.5, 16, 0x1a1a1a).setRotation(0.45),
      this.add.circle(12, -52, 3.5, 0x1a1a1a),
    ];
    const spots = [
      this.add.circle(-11, -6, 5, 0x1a1a1a),
      this.add.circle(11, -6, 5, 0x1a1a1a),
      this.add.circle(-9, 12, 4.5, 0x1a1a1a),
      this.add.circle(9, 12, 4.5, 0x1a1a1a),
    ];
    c.add([body, split, head, ...antennae, ...spots]);
    c.setScale(1.1);   // 10% bigger overall
    return c;
  }

  /** Crawl to the nearest remaining word, then eat it. Bails when the board is over or emptied. */
  private crawlToNextWord(): void {
    const bug = this.ladybird;
    if (this.advancing || !bug) return;
    const alive = this.planks.filter((p) => !p.resolved);
    if (alive.length === 0) return;   // nothing left (the board is completing)

    const idx = nearestIndex({ x: bug.x, y: bug.y }, alive.map((p) => ({ x: p.label.x, y: p.label.y })));
    const target = alive[idx];
    const dx = target.label.x - bug.x;
    const dy = target.label.y - bug.y;
    const duration = Math.max(150, (Math.hypot(dx, dy) / LADYBIRD.crawlSpeed) * 1000);
    this.tweens.add({
      targets: bug,
      x: target.label.x,
      y: target.label.y,
      rotation: Math.atan2(dy, dx) + Math.PI / 2,   // head (drawn pointing up) faces travel direction
      duration,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        if (this.advancing || target.resolved) this.crawlToNextWord();   // shot mid-crawl → next
        else this.eatWord(target);
      },
    });
  }

  /** Eat a word one letter per bite (LADYBIRD.biteMs each). Shooting it mid-meal (plank.resolved)
   *  makes the bug give up and move on — the player raced it. */
  private eatWord(plank: Plank): void {
    if (this.advancing || plank.resolved) { this.crawlToNextWord(); return; }
    const full = Array.from(plank.word.text);
    let remaining = full.length;
    const bite = (): void => {
      if (this.advancing) return;
      if (plank.resolved) { this.crawlToNextWord(); return; }
      remaining--;
      playChompSound();
      if (remaining <= 0) { this.finishEat(plank); return; }
      plank.label.setText(full.slice(0, remaining).join(''));
      this.time.delayedCall(LADYBIRD.biteMs, bite);
    };
    this.time.delayedCall(LADYBIRD.biteMs, bite);
  }

  /** A word is fully eaten: a target costs a life (and resolves toward completion so the bug can't
   *  soft-lock the board); a decoy is free. Then the bug moves on to the next word. */
  private finishEat(plank: Plank): void {
    if (plank.resolved) { if (!this.advancing) this.crawlToNextWord(); return; }
    plank.resolved = true;
    plank.label.destroy();

    const round = this.scene.get('Round') as RoundScene;
    const outcome = dropOutcome(plank.target);
    if (outcome.loseLife) round.onTargetDropped(plank.word);   // may end the round at 0 lives
    if (outcome.resolvesTarget) {
      this.rule.resolve?.(plank.word);   // eaten words leave the picture queue too — see handleFellOff
      this.refreshPictureBanner();
    }
    if (outcome.resolvesTarget && round.lives > 0 && !this.advancing) this.onTargetRemoved();
    if (round.lives > 0 && !this.advancing) this.crawlToNextWord();
  }

  private onTargetRemoved(): void {
    if (this.advancing) return;
    this.remainingTargets = Math.max(0, this.remainingTargets - 1);
    if (this.remainingTargets > 0) return;
    // Sentence boards: say what the child just built (specs/sentence-order-mode.md §11). Fired
    // here, at completion, rather than from the last tile's landing tween — Phaser destroys a
    // scene's tweens on shutdown, so on the last board of a run that callback would never run and
    // the payoff would be missing exactly where it matters most. The banner hold is what keeps the
    // scene alive for the readout (shutdown calls cancelRulePrompt), so a sentence board holds
    // longer — see SENTENCE_READOUT_EXTRA_MS.
    if (this.rule.fullSentence) speakRulePrompt(this.rule.fullSentence, i18next.language);
    this.showNextRoundBanner();
  }

  private showNextRoundBanner(): void {
    this.advancing = true;
    this.input.enabled = false;

    const round = this.scene.get('Round') as RoundScene;
    const { elapsedMs, isRecord } = round.completeBoard();
    // Every cleared round gets a sound: the big fanfare on a new best time, else a short chime.
    if (isRecord) playRecordSound();
    else playRoundCompleteSound();

    const timeText = `${(elapsedMs / 1000).toFixed(2)}s`;
    // "Round N+1" would promise a board that never comes when the App Clip's per-lesson cap has
    // just been spent (see RoundScene.hasNextBoard) — that last clear gets the time alone.
    const message = isRecord
      ? `${i18next.t('round.newRecord')}\n${timeText}`
      : round.hasNextBoard()
        ? `${i18next.t('round.next')} ${this.boardNumber + 1}\n${timeText}`
        : timeText;

    const overlay = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, message, {
      fontFamily: FONT_STACK,
      fontSize: '64px',
      color: isRecord ? '#4ecdc4' : '#ffd166',
      padding: { x: 32, y: 16 },
      align: 'center',
    }).setOrigin(0.5).setDepth(100).setAlpha(0).setScale(0.8);
    // Rounded background pill (matches the rule banner) instead of Phaser's hard-square
    // backgroundColor. Sits at overlay.depth - 1, above any leftover decoy tiles. Tweened
    // together with the text so it fades/pops as one.
    const overlayBg = addRoundedLabelBg(this, overlay, { radius: 24 }).setAlpha(0).setScale(0.8);

    this.tweens.add({
      targets: [overlay, overlayBg],
      alpha: 1,
      scale: 1,
      duration: 300,
      ease: 'Back.easeOut',
      // The hold doubles as the window the sentence readout gets before the scene restarts and
      // cancelRulePrompt() cuts it off — the plain 1.6s total clipped the tail of the last word.
      hold: 1000 + (this.rule.fullSentence ? SENTENCE_READOUT_EXTRA_MS : 0),
      yoyo: true,
      onComplete: () => {
        overlay.destroy();
        overlayBg.destroy();
        // A clean-streak milestone earns its own beat before the next board. Chained AFTER the
        // usual banner rather than replacing it, so a milestone board that is also a new record
        // still shows its record and its time.
        //
        // Both celebrations below announce themselves at BOTH ends — `celebration-start` when they
        // take the screen and `stack-celebrated` / `perfect-celebrated` when they give it back.
        // HUDScene needs the start as much as the finish: a treat flourish fires on a delay of its
        // own, and without the start beat it cannot tell "nothing is happening, flash now" from
        // "a rocket is mid-flight, wait".
        const resume = (): void => { this.input.enabled = true; round.advanceBoard(); };

        // Filling the whole stack is the run's ceiling and outranks everything else — if both land
        // on the same board, the bigger moment is the one the child gets.
        if (!round.stackCelebrated && round.sessionStars >= STAR_STACK_SLOTS) {
          round.stackCelebrated = true;
          round.events.emit('celebration-start');
          // The Satellite is earned on this very star (SAT_AT_STACK_STARS), and its flourish must
          // not land on top of the rocket. `stack-celebrated` is the beat the HUD waits for — a
          // real callback rather than a timer guessing how long confetti takes.
          this.showTrueStar(round.sessionStars, () => {
            round.events.emit('stack-celebrated');
            resume();
          });
          return;
        }

        const streak = perfectMilestone(round.cleanBoardStreak, perfectStreakStep());
        if (streak !== null) {
          round.events.emit('celebration-start');
          // Same handshake as the send-off above, for the same reason and one rung further up: the
          // 🍝 Plate is earned at star 14 (PASTA_AT_STACK_STARS), which is past the full stack but
          // NOT past this — a clean-streak milestone fires at any star, so a treat flourish can
          // still land on top of it. `perfect-celebrated` is what HUDScene waits for.
          this.showPerfectStar(streak, () => {
            round.events.emit('perfect-celebrated');
            resume();
          });
          return;
        }
        resume();
      },
    });
  }

  /**
   * The run's ceiling: every slot in the star stack filled in one game. A rocket launches up the
   * screen and confetti rains down over the message.
   *
   * Held longer than the "Perfekt!" star (this is the rarer event) and, like it, with input
   * disabled throughout — a stray tap would otherwise land on the next board the moment it appears.
   * The rocket texture is preloaded globally for the ProgressBar, so this costs no asset load.
   */
  private showTrueStar(_stars: number, done: () => void): void {
    const HOLD_MS = 3600;
    const CONFETTI = 56;
    const COLORS = [0xffd166, 0x4ecdc4, 0xff6b6b, 0x7ec4ff, 0xa0e57c, 0xd6a2ff];

    // Confetti falls over the WHOLE screen, so it is scene-level rather than parented to the
    // message container — the container is centred and would clip it to a small box.
    for (let i = 0; i < CONFETTI; i++) {
      const x = Phaser.Math.Between(0, GAME_WIDTH);
      const size = Phaser.Math.Between(8, 16);
      const flake = this.add.rectangle(
        x, Phaser.Math.Between(-260, -20), size, size * 0.6,
        COLORS[i % COLORS.length],
      ).setDepth(99).setAngle(Phaser.Math.Between(0, 360));
      this.tweens.add({
        targets: flake,
        y: GAME_HEIGHT + 60,
        x: x + Phaser.Math.Between(-90, 90),
        angle: flake.angle + Phaser.Math.Between(180, 720),
        duration: Phaser.Math.Between(2200, 3600),
        delay: Phaser.Math.Between(0, 900),
        ease: 'Sine.easeIn',
        onComplete: () => flake.destroy(),
      });
    }

    // Rocket launch: up from below the floor, out through the top.
    const rocket = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT + 120, 'rocket')
      .setOrigin(0.5).setDepth(101);
    rocket.setScale(Math.min(1, 200 / Math.max(rocket.width, rocket.height)));
    this.tweens.add({
      targets: rocket,
      y: -220,
      duration: 2300,
      ease: 'Cubic.easeIn',
      onComplete: () => rocket.destroy(),
    });
    this.tweens.add({
      targets: rocket, angle: { from: -6, to: 6 }, duration: 420, yoyo: true, repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const title = this.add.text(0, -34, i18next.t('round.trueStar'), {
      fontFamily: FONT_STACK, fontSize: '58px', color: '#ffd166', align: 'center',
      wordWrap: { width: GAME_WIDTH - 260 },
    }).setOrigin(0.5);

    // No `count` any more: the line no longer names a number, and passing one would send i18next
    // hunting for plural variants of a key that has none.
    const subtitle = this.add.text(0, 44, i18next.t('round.trueStarStreak'), {
      fontFamily: FONT_STACK, fontSize: '34px', color: '#dfe6ee', align: 'center',
      wordWrap: { width: GAME_WIDTH - 260 },
    }).setOrigin(0.5);

    // Same backing box as the "Perfekt!" star, for the same reason: gold on an arbitrary backdrop
    // is unreadable, and here there is confetti crossing behind the text as well.
    const PAD_X = 56;
    const PAD_Y = 36;
    const contentW = Math.max(title.width, subtitle.width);
    const top = title.y - title.height / 2;
    const bottom = subtitle.y + subtitle.height / 2;
    const boxW = contentW + PAD_X * 2;
    const boxH = (bottom - top) + PAD_Y * 2;
    const box = this.add.graphics();
    box.fillStyle(0x1a1f25, 0.88);
    box.fillRoundedRect(-boxW / 2, top - PAD_Y, boxW, boxH, 28);

    const group = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2, [box, title, subtitle])
      .setDepth(102).setAlpha(0).setScale(0.8);

    playRecordSound();

    this.tweens.add({
      targets: group,
      alpha: 1,
      scale: 1,
      duration: 320,
      ease: 'Back.easeOut',
      hold: HOLD_MS,
      yoyo: true,
      onComplete: () => {
        group.destroy();
        done();
      },
    });
  }

  /**
   * The accuracy reward: the ABC star with "Perfekt!" over a count of clean rounds, held for
   * ~3s and then out of the way. `abc_star` is preloaded globally (PreloadScene), so this costs
   * no asset load. Input stays disabled for the whole beat — it is a moment to look at, and a
   * stray tap here would otherwise land on the next board the instant it appears.
   */
  private showPerfectStar(streak: number, done: () => void): void {
    const HOLD_MS = 2400;   // + 300 in + 300 out ≈ the 3s beat

    const star = this.add.image(0, -70, 'abc_star').setOrigin(0.5);
    star.setScale(Math.min(1, 180 / Math.max(star.width, star.height)));

    const title = this.add.text(0, 60, i18next.t('round.perfect'), {
      fontFamily: FONT_STACK, fontSize: '64px', color: '#ffd166', align: 'center',
    }).setOrigin(0.5);

    const subtitle = this.add.text(0, 130, i18next.t('round.perfectStreak', { count: streak }), {
      fontFamily: FONT_STACK, fontSize: '34px', color: '#dfe6ee', align: 'center',
      wordWrap: { width: GAME_WIDTH - 240 },
    }).setOrigin(0.5);

    // Backing box. Without it the gold-on-white title is unreadable over a light backdrop — the
    // rule banner has the same problem and solves it with addRoundedLabelBg, but that helper only
    // measures a single Text, so the box is drawn here from the laid-out children's own bounds.
    // Semi-transparent, so the cleared board still shows through and the beat reads as an overlay
    // rather than a new screen. Added as the FIRST child, so it sits behind and tweens as one.
    const PAD_X = 56;
    const PAD_Y = 40;
    const contentW = Math.max(star.displayWidth, title.width, subtitle.width);
    const top = star.y - star.displayHeight / 2;
    const bottom = subtitle.y + subtitle.height / 2;
    const boxW = contentW + PAD_X * 2;
    const boxH = (bottom - top) + PAD_Y * 2;
    const box = this.add.graphics();
    box.fillStyle(0x1a1f25, 0.88);
    box.fillRoundedRect(-boxW / 2, top - PAD_Y, boxW, boxH, 28);

    const group = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2, [box, star, title, subtitle])
      .setDepth(100).setAlpha(0).setScale(0.8);

    this.tweens.add({
      targets: group,
      alpha: 1,
      scale: 1,
      duration: 300,
      ease: 'Back.easeOut',
      hold: HOLD_MS,
      yoyo: true,
      onComplete: () => {
        group.destroy();
        done();
      },
    });
  }
}
